import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  AvtalestatusDto,
  HalPraksisstedavtalerDto,
  PraksisavtaleValideringsfeilDto,
} from '../../api/generated/studiested-generated-api';
import {
  Table,
  TableCellTd,
  TableCellTh,
  TableHeader,
  TableMain,
  TableRow,
} from '../../components/table/alpTable';
import { useIntl } from 'react-intl';
import translations from './agreementtabs.i18n';
import { useLoader } from '../../api/useLoader';
import Spinner from '../../components/spinner/spinner';
import { formatLocaleNo, sortDates } from '../../date.utils';
import Button from '../../components/button';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { mapUndefinedToEmptyString } from '../../util/stringutils';
import * as css from './agreementtabs.module.scss';
import Icon, { IconType } from '../../components/icon';
import { useNavigate } from 'react-router-dom';

const filterOnStatuses = (
  avtaler: AvtaleTilSigneringDto[],
  statuses: AvtalestatusDto[],
) => avtaler.filter((avtale) => statuses.includes(avtale.status));

// This does not belong in the API contract.
export interface AvtaleTilSigneringDto {
  id: string;
  navn: string | undefined;
  ansvarlig: string | undefined;
  parter: number | undefined;
  utloepsdato: string | undefined;
  status: AvtalestatusDto;
  valideringsfeil: PraksisavtaleValideringsfeilDto[];
}

const mapToAvtaleForSignering = (
  avtaler: HalPraksisstedavtalerDto | undefined,
): AvtaleTilSigneringDto[] =>
  avtaler?.avtaler?.map((avtale) => ({
    id: avtale.avtaleId,
    navn: avtale.avtalenavn,
    ansvarlig: avtale.saksansvarlig,
    parter: avtale.avtaleparter,
    status: avtale.avtalestatus,
    utloepsdato: avtale.avtaleslutt,
    valideringsfeil: avtale.valideringsfeil,
  })) ?? [];

type FilteredAvtalerProps = {
  filterByStatuses: AvtalestatusDto[];
};

const FilteredAvtaler: React.FC<
  React.PropsWithChildren<FilteredAvtalerProps>
> = ({ filterByStatuses }) => {
  const { praksisstedApi } = useContext(StudiestedApiContext);
  const { data: avtaler, loading } = useLoader(
    () => praksisstedApi.avtalerPraksisGet(),
    [],
  );

  const filteredAvtaler = useMemo<AvtaleTilSigneringDto[]>(
    () =>
      avtaler
        ? filterOnStatuses(mapToAvtaleForSignering(avtaler), filterByStatuses)
        : [],
    [avtaler, filterByStatuses],
  );

  return loading ? <Spinner /> : <AgreementsTable avtaler={filteredAvtaler} />;
};

export const AgreementsPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => (
  <FilteredAvtaler
    filterByStatuses={[
      AvtalestatusDto.Aktiv,
      AvtalestatusDto.IkkeAktiv,
      AvtalestatusDto.InnhenterOpplysninger,
      AvtalestatusDto.Kansellert,
      AvtalestatusDto.SendtTilSignering,
      AvtalestatusDto.Avsluttet,
      AvtalestatusDto.Feil,
    ]}
  />
);

export const AgreementsDraftsPage: React.FC<
  React.PropsWithChildren<unknown>
> = () => (
  <FilteredAvtaler
    filterByStatuses={[AvtalestatusDto.Utkast, AvtalestatusDto.Feil]}
  />
);

enum AgreementColumns {
  NAVN,
  ANSVARLIG,
  PARTER,
  STATUS,
  UTLOEPSDATO,
  HANDLING,
}

type AgreementsTableProps = {
  avtaler: AvtaleTilSigneringDto[];
};

const AgreementsTable: React.FC<
  React.PropsWithChildren<AgreementsTableProps>
> = ({ avtaler }) => {
  const navigate = useNavigate();
  const { formatMessage, locale } = useIntl();
  const translateStatusToLabel = useCallback(
    (status: AvtalestatusDto) => {
      switch (status) {
        case AvtalestatusDto.Aktiv:
          return formatMessage({ id: translations.statusAktiv.id });
        case AvtalestatusDto.SendtTilSignering:
          return formatMessage({ id: translations.statusIkkeSignert.id });
        case AvtalestatusDto.Avsluttet:
          return formatMessage({ id: translations.statusAvsluttet.id });
        case AvtalestatusDto.InnhenterOpplysninger:
          return formatMessage({
            id: translations.statusInnhenterOpplysninger.id,
          });
        case AvtalestatusDto.Kansellert:
          return formatMessage({ id: translations.statusKansellert.id });
        case AvtalestatusDto.Utkast:
          return formatMessage({ id: translations.statusUtkast.id });
        case AvtalestatusDto.IkkeAktiv:
          return formatMessage({ id: translations.statusInaktiv.id });
        case AvtalestatusDto.Feil:
          return formatMessage({ id: translations.statusFeil.id });
      }
    },
    [formatMessage],
  );

  const compare = Intl.Collator(locale || 'nb').compare;
  const sort = useCallback(
    (
      col: AgreementColumns,
      a: AvtaleTilSigneringDto,
      b: AvtaleTilSigneringDto,
    ) => {
      const [navnA, navnB, ansvarligA, ansvarligB] = mapUndefinedToEmptyString(
        a.navn,
        b.navn,
        a.ansvarlig,
        b.ansvarlig,
      );

      const [parterA, parterB] = [a.parter || 0, b.parter || 0];

      switch (col) {
        case AgreementColumns.NAVN:
          return compare(navnA, navnB);
        case AgreementColumns.ANSVARLIG:
          return compare(ansvarligA, ansvarligB);
        case AgreementColumns.PARTER:
          return parterA > parterB ? 1 : parterA < parterB ? -1 : 0;
        case AgreementColumns.STATUS:
          return compare(
            translateStatusToLabel(a.status),
            translateStatusToLabel(b.status),
          );
        case AgreementColumns.UTLOEPSDATO:
          return sortDates(a.utloepsdato, b.utloepsdato);
      }
      return 0;
    },
    [compare, translateStatusToLabel],
  );

  const [activeCol, setActiveCol] = useState<number>(AgreementColumns.NAVN);
  const [sortedAgreements, setSortedAgreements] = useState<
    AvtaleTilSigneringDto[]
  >(avtaler.sort((a, b) => sort(AgreementColumns.NAVN, a, b)));
  const [search, setSearch] = useState<{ [column: number]: string }>({
    [AgreementColumns.NAVN]: '',
    [AgreementColumns.ANSVARLIG]: '',
  });

  const sortAgreementsByActiveCol = useCallback(
    (col: AgreementColumns, ascending: boolean) => {
      setSortedAgreements(
        [...avtaler].sort((a, b) =>
          ascending ? sort(col, b, a) : sort(col, a, b),
        ),
      );
    },
    [avtaler, sort],
  );

  const tableHeaders = useMemo(
    () => ({
      [AgreementColumns.NAVN]: formatMessage({
        id: translations.navnHeader.id,
      }),
      [AgreementColumns.ANSVARLIG]: formatMessage({
        id: translations.ansvarligHeader.id,
      }),
      [AgreementColumns.PARTER]: formatMessage({
        id: translations.parterHeader.id,
      }),
      [AgreementColumns.STATUS]: formatMessage({
        id: translations.statusHeader.id,
      }),
      [AgreementColumns.UTLOEPSDATO]: formatMessage({
        id: translations.utloepsdatoHeader.id,
      }),
      [AgreementColumns.HANDLING]: formatMessage({
        id: translations.handlingHeader.id,
      }),
    }),
    [formatMessage],
  );

  const headers = useMemo(() => {
    const canSearch = [AgreementColumns.NAVN, AgreementColumns.ANSVARLIG];

    return Object.entries(tableHeaders).map(([type, title], index) => (
      <TableCellTh
        key={title}
        colIndex={index}
        activeCol={index === activeCol}
        setActiveCol={setActiveCol}
        search={(_, value) => setSearch({ ...search, [type]: value })}
        canSearch={canSearch.includes(index)}
        sort={sortAgreementsByActiveCol}
        canSort={index !== AgreementColumns.HANDLING}
      >
        {title}
      </TableCellTh>
    ));
  }, [tableHeaders, activeCol, search, sortAgreementsByActiveCol]);

  const handleSearchFilter = useCallback(
    (avtalerTilSok: AvtaleTilSigneringDto[]) =>
      avtalerTilSok.filter((avtale) => {
        const navn = `${avtale.navn || ''}`.toLowerCase();
        const ansvarlig = `${avtale.ansvarlig || ''}`.toLowerCase();

        const navnSearch = search[AgreementColumns.NAVN];
        const ansvarligSearch = search[AgreementColumns.ANSVARLIG];

        const filterNavn =
          navnSearch.length === 0 ||
          navn.indexOf(navnSearch.toLowerCase()) > -1;

        const filterAnsvarlig =
          ansvarligSearch.length === 0 ||
          ansvarlig.indexOf(ansvarligSearch.toLowerCase()) > -1;

        return filterNavn && filterAnsvarlig;
      }),
    [search],
  );

  const filteredAndSortedRows = useMemo<AvtaleTilSigneringDto[]>(
    () => handleSearchFilter(sortedAgreements),
    [sortedAgreements, handleSearchFilter],
  );

  const rows = useMemo(
    () =>
      filteredAndSortedRows.map((row) => {
        const {
          valideringsfeil,
          navn,
          ansvarlig,
          parter,
          utloepsdato,
          status,
          id,
        } = row;

        const hasValideringsfeil = !!valideringsfeil.length;

        return (
          <TableRow
            key={id}
            rowBackground={hasValideringsfeil ? 'alert' : 'default'}
          >
            <TableCellTd header={tableHeaders[AgreementColumns.NAVN]}>
              {navn}
            </TableCellTd>
            <TableCellTd header={tableHeaders[AgreementColumns.ANSVARLIG]}>
              {ansvarlig}
            </TableCellTd>
            <TableCellTd header={tableHeaders[AgreementColumns.PARTER]}>
              {parter}
            </TableCellTd>
            <TableCellTd header={tableHeaders[AgreementColumns.STATUS]}>
              <StatusCellContent
                label={translateStatusToLabel(status)}
                valideringsfeil={valideringsfeil}
              />
            </TableCellTd>
            <TableCellTd header={tableHeaders[AgreementColumns.UTLOEPSDATO]}>
              {utloepsdato
                ? formatLocaleNo(utloepsdato)
                : formatMessage({ id: translations.ingen.id })}
            </TableCellTd>
            <TableCellTd header={tableHeaders[AgreementColumns.HANDLING]}>
              <Button.Secondary
                role={'link'}
                onClick={() =>
                  navigate(`/practiceplaces/practiceplaceagreement/${row.id}`)
                }
              >
                {formatMessage({ id: translations.btnHandling.id })}
              </Button.Secondary>
            </TableCellTd>
          </TableRow>
        );
      }),
    [
      filteredAndSortedRows,
      tableHeaders,
      translateStatusToLabel,
      formatMessage,
      navigate,
    ],
  );

  return (
    <Table>
      <TableHeader>
        <TableRow top>{headers}</TableRow>
      </TableHeader>
      <TableMain>{rows}</TableMain>
    </Table>
  );
};

const StatusCellContent: React.FC<
  React.PropsWithChildren<{
    label: string;
    valideringsfeil: PraksisavtaleValideringsfeilDto[];
  }>
> = ({ label, valideringsfeil }) => {
  const hasValideringsfeil = valideringsfeil.length > 0;

  return (
    <div
      className={css.statusCell}
      title={
        hasValideringsfeil
          ? valideringsfeil.map((feil) => feil.message).join(', ')
          : undefined
      }
    >
      {hasValideringsfeil && <Icon type={IconType.Warning} />}
      {label}
    </div>
  );
};
